import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { TourDriverConfig, TourDriverStep } from './types';
import { computePlacement, ComputedPlacement } from './placement';
import TourOverlay from './TourOverlay';
import TourPopover from './TourPopover';

type ActiveTour = {
  steps: TourDriverStep[];
  activeIndex: number;
  config: TourDriverConfig;
};

export type TourDriverReturn = {
  /** Include this in your component's JSX — renders the overlay + popover portal */
  portal: React.ReactPortal | null;
  /** Start a tour. Calling again while a tour is running replaces it. */
  start: (config: TourDriverConfig) => void;
  /** Stop the current tour without triggering onDestroyStarted */
  stop: () => void;
  /** Advance one step (or close on the last step) */
  moveNext: () => void;
  /** Go back one step */
  movePrev: () => void;
  /** Index of the currently shown step, or undefined if no tour is running */
  getActiveIndex: () => number | undefined;
  /** Whether a tour is currently running */
  isActive: boolean;
};

/**
 * React-native tour driver. No external dependencies — positions popovers
 * with manual placement math and renders via a portal.
 *
 * Usage:
 *   const { portal, start, stop, moveNext, getActiveIndex, isActive } = useTourDriver();
 *   // Include portal in your JSX:
 *   return <>{children}{portal}</>;
 *   // Start a tour:
 *   start({ steps, onDestroyStarted: () => { ... }, onNextClick: () => { ... } });
 */
function useTourDriver(): TourDriverReturn {
  const [tour, setTour] = useState<ActiveTour | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [placement, setPlacement] = useState<ComputedPlacement | null>(null);

  const popoverRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  // ─── resolve the active element ──────────────────────────────────────────

  const getElement = useCallback((step: TourDriverStep): HTMLElement | null => {
    if (!step?.element) return null;
    if (typeof step.element === 'string') return document.querySelector<HTMLElement>(step.element);
    return step.element;
  }, []);

  // ─── track target rect with a rAF loop ───────────────────────────────────

  useEffect(() => {
    if (!tour) {
      cancelAnimationFrame(rafRef.current);
      observerRef.current?.disconnect();
      setTargetRect(null);
      return;
    }

    const el = getElement(tour.steps[tour.activeIndex]);

    const measure = () => {
      setTargetRect(el ? el.getBoundingClientRect() : null);
    };

    const tick = () => {
      measure();
      rafRef.current = requestAnimationFrame(tick);
    };

    observerRef.current?.disconnect();
    if (el) {
      observerRef.current = new ResizeObserver(measure);
      observerRef.current.observe(el);
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      observerRef.current?.disconnect();
    };
  }, [tour?.activeIndex, getElement]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── recompute popover placement after paint ──────────────────────────────

  useLayoutEffect(() => {
    if (!tour || !popoverRef.current) {
      setPlacement(null);
      return;
    }

    const { offsetWidth: w, offsetHeight: h } = popoverRef.current;
    const step = tour.steps[tour.activeIndex];

    if (targetRect) {
      setPlacement(computePlacement(targetRect, w, h, step.popover?.side, step.popover?.align));
    } else {
      setPlacement({
        top: Math.max(8, window.innerHeight / 2 - h / 2),
        left: Math.max(8, window.innerWidth / 2 - w / 2),
        side: 'bottom',
      });
    }
  }, [targetRect, tour?.activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── scroll active element into view ─────────────────────────────────────

  useEffect(() => {
    if (!tour) return;
    getElement(tour.steps[tour.activeIndex])?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [tour?.activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── actions ─────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    setTour((prev) => {
      prev?.config.onDestroyStarted?.();
      return null;
    });
  }, []);

  const handleNext = useCallback(() => {
    setTour((prev) => {
      if (!prev) return null;

      if (prev.config.onNextClick) {
        prev.config.onNextClick();
        return prev; // caller controls step advancement
      }

      const next = prev.activeIndex + 1;
      if (next >= prev.steps.length) {
        prev.config.onDestroyStarted?.();
        return null;
      }
      return { ...prev, activeIndex: next };
    });
  }, []);

  const handlePrev = useCallback(() => {
    setTour((prev) => {
      if (!prev) return null;

      if (prev.config.onPrevClick) {
        prev.config.onPrevClick();
        return prev;
      }

      return { ...prev, activeIndex: Math.max(0, prev.activeIndex - 1) };
    });
  }, []);

  // ─── keyboard ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!tour) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
      else if (e.key === 'ArrowRight') handleNext();
      else if (e.key === 'ArrowLeft') handlePrev();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tour, handleClose, handleNext, handlePrev]);

  // ─── portal ──────────────────────────────────────────────────────────────

  const portal = useMemo(() => {
    if (!tour) return null;

    const step = tour.steps[tour.activeIndex];

    return createPortal(
      <>
        <TourOverlay targetRect={targetRect} onClick={handleClose} />
        <TourPopover
          ref={popoverRef}
          title={step.popover?.title}
          description={step.popover?.description}
          stepIndex={tour.activeIndex}
          totalSteps={tour.steps.length}
          showProgress={tour.config.showProgress ?? tour.steps.length > 1}
          isFirst={tour.activeIndex === 0}
          isLast={tour.activeIndex === tour.steps.length - 1}
          placement={placement}
          onNext={handleNext}
          onPrev={handlePrev}
          onClose={handleClose}
        />
      </>,
      document.body,
    );
  }, [tour, targetRect, placement, handleClose, handleNext, handlePrev]);

  // ─── public API ──────────────────────────────────────────────────────────

  const start = useCallback((config: TourDriverConfig) => {
    setTour({ steps: config.steps, activeIndex: 0, config });
    setPlacement(null);
  }, []);

  const stop = useCallback(() => setTour(null), []);

  const getActiveIndex = useCallback(() => tour?.activeIndex, [tour]);

  return {
    portal,
    start,
    stop,
    moveNext: handleNext,
    movePrev: handlePrev,
    getActiveIndex,
    isActive: tour !== null,
  };
}

export default useTourDriver;
